import type React from 'react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Input } from 'antd'
import { Search } from 'lucide-react'
import { cn } from '@/utils/css'
import styles from './top.module.less'
import NewChatIcon from '@/assets/images/new_chat_icon.svg'

interface ChatSearchProps {
  onSearch?: (value: string) => void
  onNewChat?: () => void
}

const ChatSearch: React.FC<ChatSearchProps> = ({ onSearch, onNewChat }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef<any>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleSearch = useCallback(
    (value: string) => {
      onSearch?.(value)
    },
    [onSearch],
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchValue && searchValue.length > 0) {
        return
      }
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false)
        setSearchValue('')
        handleSearch('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleSearch, searchValue])

  const handleSearchClick = () => {
    setIsExpanded(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 300)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    handleSearch(e.target.value)
  }

  const handlePressEnter = () => {
    handleSearch(searchValue)
  }

  return (
    <div className="h-20 px-4 py-5 flex items-center justify-center flex-row">
      <div className="flex-1 flex items-center relative" ref={wrapperRef}>
        <div
          className={cn(
            'flex flex-1 justify-center items-center h-10 font-medium text-sm rounded-lg transition-all duration-300 ease-in-out mr-2 gap-2 cursor-pointer bg-gradient-to-r from-[#004FF8] to-[#C17BFF] text-white hover:opacity-80',
            isExpanded &&
              'opacity-0 transform translate-x-10 m-0 p-0 overflow-hidden w-0',
          )}
          onClick={onNewChat}
        >
          <img src={NewChatIcon} width={16} height={16}></img>
          <span>新建对话</span>
        </div>
        <div
          className={cn(styles.searchWrapper, {
            [styles.expanded]: isExpanded,
          })}
        >
          <Input
            ref={inputRef}
            allowClear
            prefix={<Search size={16} color="#1C1F23" />}
            styles={{
              prefix: {
                width: '40px',
                height: '40px',
                display: 'flex',
                justifyContent: 'center',
                marginInlineEnd: '0',
              },
            }}
            className={cn(styles.searchInput, {
              [styles.expanded]: isExpanded,
            })}
            placeholder={isExpanded ? '搜索对话记录' : ''}
            value={searchValue}
            onChange={handleInputChange}
            onPressEnter={handlePressEnter}
          />
          <div
            className={cn(styles.searchButton, { [styles.hidden]: isExpanded })}
            onClick={handleSearchClick}
          >
            <Search size={16} color="#1C1F23" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatSearch
